import { useMemo } from 'react';
import type {
  ChatWidgetProps,
  AvailabilityConfig,
  HandoffConfig,
  SessionConfig,
  PreChatFormConfig,
  ConsentConfig,
  IdleTimeoutConfig,
  AutoOpenRules,
} from '../types';

type ResolvePropFn = <T>(prop: T | undefined, paths: string[], fallback: T) => T;
type ReadSettingFn = (path: string) => unknown;

interface UseWidgetBehaviorOptions {
  props: ChatWidgetProps;
  resolveProp: ResolvePropFn;
  readSetting: ReadSettingFn;
  sessionHistoryEnabled: boolean;
}

export function useWidgetBehavior({
  props,
  resolveProp,
  readSetting,
  sessionHistoryEnabled,
}: UseWidgetBehaviorOptions) {
  // Behavior
  const subjectProp = resolveProp(props.subject, ['subject'], undefined);
  const channel = resolveProp(props.channel, ['channel'], 'chat');
  const threadIdProp = resolveProp(props.threadId, ['threadId'], undefined);
  const threadIdControlled = props.threadId !== undefined;
  const threadPollIntervalMs = resolveProp(props.threadPollIntervalMs, ['threadPollIntervalMs'], 0);
  const department = resolveProp(props.department, ['department'], 'Customer Support');
  const rawCustomFields = resolveProp(props.customFields, ['customFields'], {});
  const customFields = useMemo(
    () => (rawCustomFields && typeof rawCustomFields === 'object' ? rawCustomFields : {}),
    [rawCustomFields]
  );
  const rawRecords = resolveProp(props.records, ['records'], {});
  const records = useMemo(
    () => (rawRecords && typeof rawRecords === 'object' ? rawRecords : {}),
    [rawRecords]
  );
  const persistThread = resolveProp(
    props.persistThread,
    ['persistThread', 'session.persistThread'],
    true
  );
  const effectivePersistThread = sessionHistoryEnabled ? false : persistThread;

  // Layout
  const position = resolveProp(props.position, ['behavior.position', 'position'], 'bottom-right');
  const zIndex = resolveProp(props.zIndex, ['behavior.zIndex', 'zIndex'], 1000);
  const showViewTabs = resolveProp(
    props.showViewTabs,
    ['behavior.showViewTabs', 'showViewTabs'],
    true
  );
  const showTimestamps = resolveProp(
    props.showTimestamps,
    ['behavior.showTimestamps', 'showTimestamps'],
    false
  );

  // Limits
  const maxInputLength = resolveProp(
    props.maxInputLength,
    ['limits.maxInputLength', 'maxInputLength'],
    undefined
  );
  const inputWarningThreshold = resolveProp(
    props.inputWarningThreshold,
    ['limits.inputWarningThreshold', 'inputWarningThreshold'],
    undefined
  );
  const maxMessagesPerSession = resolveProp(
    props.maxMessagesPerSession,
    ['limits.maxMessagesPerSession', 'maxMessagesPerSession'],
    undefined
  );
  const rateLimit = resolveProp(props.rateLimit, ['limits.rateLimit', 'rateLimit'], undefined);
  const maxFileSizeMB = resolveProp(
    props.maxFileSizeMB,
    ['limits.maxFileSizeMB', 'maxFileSizeMB'],
    undefined
  );
  const maxFileCount = resolveProp(
    props.maxFileCount,
    ['limits.maxFileCount', 'maxFileCount'],
    undefined
  );
  const allowedFileTypes = resolveProp(
    props.allowedFileTypes,
    ['limits.allowedFileTypes', 'allowedFileTypes'],
    undefined
  );
  const allowedDomains = resolveProp(
    props.allowedDomains,
    ['security.allowedDomains', 'allowedDomains'],
    []
  );
  const allowedOrigins = resolveProp(
    props.allowedOrigins,
    ['security.allowedOrigins', 'allowedOrigins'],
    []
  );

  // Complex config objects
  const availability = useMemo(
    () =>
      ({
        ...(readSetting('availability') || {}),
        ...(props.availability || {}),
      }) as Partial<AvailabilityConfig>,
    [readSetting, props.availability]
  );
  const offlineMessage = resolveProp(props.offlineMessage, ['offlineMessage'], '');
  const handoff = useMemo(
    () =>
      ({ ...(readSetting('handoff') || {}), ...(props.handoff || {}) }) as Partial<HandoffConfig>,
    [readSetting, props.handoff]
  );
  const sessionConfig = useMemo(
    () =>
      ({ ...(readSetting('session') || {}), ...(props.session || {}) }) as Partial<SessionConfig>,
    [readSetting, props.session]
  );
  const autoOpenRules = useMemo(
    () =>
      ({
        ...(readSetting('autoOpenRules') || {}),
        ...(props.autoOpenRules || {}),
      }) as Partial<AutoOpenRules>,
    [readSetting, props.autoOpenRules]
  );
  const autoOpenDelayMs = resolveProp(
    props.autoOpenDelayMs,
    ['behavior.autoOpenDelayMs', 'autoOpenDelayMs'],
    undefined
  );
  const preChatForm = useMemo(
    () =>
      ({
        ...(readSetting('preChatForm') || {}),
        ...(props.preChatForm || {}),
      }) as Partial<PreChatFormConfig>,
    [readSetting, props.preChatForm]
  );
  const consent = useMemo(
    () =>
      ({
        ...(readSetting('security.consent') || readSetting('consent') || {}),
        ...(props.consent || {}),
      }) as Partial<ConsentConfig>,
    [readSetting, props.consent]
  );

  // Security / misc
  const doNotTrain = resolveProp(
    props.doNotTrain,
    ['security.doNotTrain', 'doNotTrain'],
    undefined
  );
  const retentionDays = resolveProp(
    props.retentionDays,
    ['security.retentionDays', 'retentionDays'],
    undefined
  );
  const sessionVariables = resolveProp(props.sessionVariables, ['sessionVariables'], undefined);
  const retryPolicy = resolveProp(props.retryPolicy, ['retryPolicy'], undefined);
  const lazyLoad = resolveProp(props.lazyLoad, ['behavior.lazyLoad', 'lazyLoad'], false);
  const idleTimeout = useMemo(
    () =>
      ({
        ...(readSetting('idleTimeout') || {}),
        ...(props.idleTimeout || {}),
      }) as Partial<IdleTimeoutConfig>,
    [readSetting, props.idleTimeout]
  );

  return {
    subjectProp,
    channel,
    threadIdProp,
    threadIdControlled,
    threadPollIntervalMs,
    department,
    customFields,
    records,
    persistThread,
    effectivePersistThread,
    position,
    zIndex,
    showViewTabs,
    showTimestamps,
    maxInputLength,
    inputWarningThreshold,
    maxMessagesPerSession,
    rateLimit,
    maxFileSizeMB,
    maxFileCount,
    allowedFileTypes,
    allowedDomains,
    allowedOrigins,
    availability,
    offlineMessage,
    handoff,
    sessionConfig,
    autoOpenRules,
    autoOpenDelayMs,
    preChatForm,
    consent,
    doNotTrain,
    retentionDays,
    sessionVariables,
    retryPolicy,
    lazyLoad,
    idleTimeout,
  };
}
